import { lazy, Suspense, useEffect, useRef, ReactNode } from 'react';
import {
	BrowserRouter as Router,
	Routes,
	Route,
	useLocation,
} from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import ErrorBoundary from './components/common/ErrorBoundary';
import CookieConsent from './components/layout/CookieConsent';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import PageSkeleton from './components/common/PageSkeleton';
import PageTransition from './components/common/PageTransition';
import SkipToContent from './components/common/SkipToContent';
import DynamicFavicon from './components/common/DynamicFavicon';
import { useScrollRestoration } from './hooks/useScrollRestoration';
import { analytics } from './services/analytics';
import { getPageBackgroundClass } from './utils/pageBackgrounds';
import './pages/pages.css';

// Lazy load pages for code splitting
const Home = lazy(() => import('./pages/Home'));
const Services = lazy(() => import('./pages/Services'));
const Gallery = lazy(() => import('./pages/Gallery'));
const Testimonials = lazy(() => import('./pages/Testimonials'));
const Contact = lazy(() => import('./pages/Contact'));
const FAQ = lazy(() => import('./pages/FAQ'));
const AreasCovered = lazy(() => import('./pages/AreasCovered'));
const NotFound = lazy(() => import('./pages/NotFound'));
const ThankYou = lazy(() => import('./pages/ThankYou'));

function MainWithBackground({ children }: { children: ReactNode }) {
	const location = useLocation();
	const mainRef = useRef<HTMLElement>(null);

	// Apply page background class based on current route
	useEffect(() => {
		const mainElement = mainRef.current;
		if (!mainElement) {
			console.warn('PageBackground: main element not found');
			return;
		}

		const backgroundClass = getPageBackgroundClass(location.pathname);
		console.log('PageBackground: Applying class', backgroundClass, 'for path', location.pathname);
		
		// Remove all page background classes first
		const allBgClasses = Array.from(mainElement.classList).filter((cls) =>
			cls.startsWith('page-bg-')
		);
		mainElement.classList.remove(...allBgClasses);
		
		// Add the current page's background class
		mainElement.classList.add(backgroundClass);
		
		// Verify it was added
		console.log('PageBackground: Main element classes:', Array.from(mainElement.classList));
	}, [location.pathname]);

	return (
		<main id="main-content" ref={mainRef}>
			{children}
		</main>
	);
}

function AppRoutes() {
	const location = useLocation();
	useScrollRestoration(true);

	// Track page views on route changes
	useEffect(() => {
		analytics.trackPageView(location.pathname + location.search, document.title);
	}, [location.pathname, location.search]);

	return (
		<AnimatePresence mode="wait" initial={false}>
			<Suspense fallback={<PageSkeleton />}>
				<PageTransition key={location.pathname}>
					<Routes location={location}>
						<Route path="/" element={<Home />} />
						<Route path="/services" element={<Services />} />
						<Route path="/gallery" element={<Gallery />} />
						<Route path="/testimonials" element={<Testimonials />} />
						<Route path="/contact" element={<Contact />} />
						<Route path="/faq" element={<FAQ />} />
						<Route path="/areas-covered" element={<AreasCovered />} />
						<Route path="/thank-you" element={<ThankYou />} />
						<Route path="*" element={<NotFound />} />
					</Routes>
				</PageTransition>
			</Suspense>
		</AnimatePresence>
	);
}

function App() {
	// Enable browser scroll restoration
	useEffect(() => {
		if ('scrollRestoration' in window.history) {
			window.history.scrollRestoration = 'manual';
		}
	}, []);

	return (
		<ErrorBoundary>
			<DynamicFavicon />
			<CookieConsent />
			<Router
				future={{
					v7_startTransition: true,
					v7_relativeSplatPath: true,
				}}
			>
				<div className="app">
					<SkipToContent />
					<Header />
					<MainWithBackground>
						<AppRoutes />
					</MainWithBackground>
					<Footer />
				</div>
			</Router>
		</ErrorBoundary>
	);
}

export default App;
